import * as Sentry from "@sentry/react";
import clsx from "clsx";

import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/ui/button/Button";
import { messageFromError } from "~/helpers/messageFromError";

import { DecodedWidgetConfig } from "../../types/DecodedWidgetConfig.type";
import {
  RenderableWidget,
  RenderableWidgetOrError,
} from "../../types/RenderableWidget.type";
import { googleSearchConsoleDataAdapter } from "../DashboardView/utils/googleSearchConsoleDataAdapter";
import { DynamicSettingsIcon } from "../DynamicSettingIcon";
import { RenderGoogleAnalyticsReport } from "../RenderGoogleAnalyticsReport/RenderGoogleAnalyticsReport";
import { WidgetErrorView } from "../WidgetErrorView/WidgetErrorView";
import { WidgetSettingsForm } from "../WidgetSettingsForm/WidgetSettingsForm";
import styles from "./DashboardViewWidget.module.scss";

interface DashboardViewWidgetProps {
  widget: RenderableWidgetOrError;
  lockScrolling: boolean;
  showDragHandle: boolean;
  isEditing: boolean;
  onEditToggle: (widgetId: string) => void;
  updateSingleWidget: (config: DecodedWidgetConfig) => Promise<void>;
}

export const DashboardViewWidget = ({
  widget,
  lockScrolling,
  showDragHandle,
  isEditing,
  onEditToggle,
  updateSingleWidget,
}: DashboardViewWidgetProps) => {
  return (
    <div key={widget.id} className={styles.widget}>
      <div
        className={clsx(styles.widgetContent, {
          [styles.lockScrolling]: lockScrolling,
        })}
      >
        {(() => {
          if ("error" in widget) {
            return (
              <WidgetErrorView className={styles.widgetContentError}>
                {messageFromError(widget.error)}
              </WidgetErrorView>
            );
          }

          return isEditing ? (
            <Sentry.ErrorBoundary
              key="form-error-boundary"
              fallback={({ error }) => (
                <WidgetErrorView className={styles.widgetContentError}>
                  {messageFromError(error)}
                </WidgetErrorView>
              )}
            >
              <WidgetSettingsForm
                config={widget.config}
                updateConfig={updateSingleWidget}
                onEditingCompleted={() => onEditToggle(widget.id)}
              />
            </Sentry.ErrorBoundary>
          ) : (
            <Sentry.ErrorBoundary
              key="widget-error-boundary"
              fallback={({ error }) => (
                <WidgetErrorView className={styles.widgetContentError}>
                  {messageFromError(error)}
                </WidgetErrorView>
              )}
            >
              <RenderWidget widget={widget} />
            </Sentry.ErrorBoundary>
          );
        })()}
      </div>
      <div className={styles.widgetActionsPanel}>
        <Button
          type="button"
          unstyled
          noHover
          onClick={() => onEditToggle(widget.id)}
          className={styles.widgetActionsPanelButton}
        >
          <DynamicSettingsIcon isEditing={isEditing} />
        </Button>
      </div>

      <div
        className={clsx("rgl-drag-handle", styles.rglDragHandle, {
          [styles.rglDragHandleHidden]: !showDragHandle,
        })}
      >
        <Button type="button" outlined>
          <Icon iconName="FourHeadedArrowMoveIcon" width="4rem" height="4rem" />
        </Button>
      </div>
    </div>
  );
};

interface RenderWidgetProps {
  widget: RenderableWidget;
}

export const RenderWidget = ({ widget }: RenderWidgetProps) => {
  return (
    <div className={styles.renderWidget}>
      {widget.config.metadata.title && (
        <h2 className={styles.renderWidgetTitle}>
          {widget.config.metadata.title}
        </h2>
      )}
      <div
        className={clsx(styles.renderWidgetContent, {
          [styles.renderWidgetContentWithTitle]: Boolean(
            widget.config.metadata.title,
          ),
        })}
      >
        {(() => {
          switch (widget.type) {
            case "property_report":
              return (
                <RenderGoogleAnalyticsReport
                  viewType={widget.config.metadata.viewType}
                  data={widget.data}
                />
              );
            case "property_realtime_report":
              return (
                <RenderGoogleAnalyticsReport
                  viewType={widget.config.metadata.viewType}
                  data={widget.data}
                />
              );

            case "search_analytics":
              return (
                <RenderGoogleAnalyticsReport
                  viewType={widget.config.metadata.viewType}
                  data={googleSearchConsoleDataAdapter(
                    widget.data,
                    widget.config.query,
                  )}
                />
              );
            default:
              throw new Error(
                `Unsupported widget type. widget=${JSON.stringify(widget).slice(0, 100)}`,
              );
          }
        })()}
      </div>
    </div>
  );
};
